import { useState } from "react";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import Breadcrumb from "@/components/base/Breadcrumb";
import ScrollToTop from "@/components/base/ScrollToTop";
import { aboutData } from "@/mocks/aboutData";

export default function AboutPage() {
  const [contactForm, setContactForm] = useState({ name: "", email: "", message: "" });
  const [contactStatus, setContactStatus] = useState<"idle" | "submitting" | "success" | "error">("idle");
  const [contactError, setContactError] = useState("");

  const handleContactSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const honeypot = (form.querySelector('[name="mobile_alt"]') as HTMLInputElement)?.value?.trim();
    if (honeypot) {
      setContactStatus("success");
      setContactForm({ name: "", email: "", message: "" });
      return;
    }
    setContactStatus("submitting");
    setContactError("");
    try {
      const formData = new FormData(form);
      const res = await fetch("https://readdy.ai/api/form/d9s9a9fnahb7382ovshg", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const text = await res.text();
      let parsed: Record<string, unknown> = {};
      try { parsed = JSON.parse(text); } catch { /* ignore */ }
      if (res.ok && (parsed as { code?: string })?.code === "OK") {
        setContactStatus("success");
        setContactForm({ name: "", email: "", message: "" });
      } else {
        const msg = (parsed as { meta?: { message?: string } })?.meta?.message
          || (parsed as { message?: string })?.message
          || (parsed as { meta?: { detail?: string } })?.meta?.detail
          || text
          || "提交失败，请稍后再试";
        setContactError(String(msg).includes("spam") ? "提交失败，请稍后再试" : String(msg));
        setContactStatus("error");
      }
    } catch {
      setContactError("网络错误，请检查连接后重试");
      setContactStatus("error");
    }
  };

  return (
    <>
      <ScrollToTop />
      <Navbar />
      <main className="min-h-screen pt-20">
        {/* Hero */}
        <section className="w-full relative h-[300px] md:h-[400px] overflow-hidden">
          <img
            src={aboutData.hero.image}
            alt="关于媚肉之香"
            className="w-full h-full object-cover object-top"
            title="媚肉之香品牌介绍"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/50"></div>
          <div className="absolute inset-0 flex flex-col items-center justify-center px-6">
            <h1 className="font-heading text-3xl md:text-5xl font-semibold text-background-50 mb-3">
              {aboutData.hero.title}
            </h1>
            <p className="text-sm md:text-base text-background-200">{aboutData.hero.subtitle}</p>
          </div>
        </section>

        <section className="w-full px-6 md:px-10 py-6 bg-background-50">
          <div className="max-w-7xl mx-auto">
            <Breadcrumb items={[{ label: "关于我们" }]} />
          </div>
        </section>

        {/* Brand Introduction */}
        <section id="philosophy" className="w-full px-6 md:px-10 py-14 md:py-20 bg-background-50">
          <div className="max-w-4xl mx-auto">
            <h2 className="font-heading text-2xl md:text-3xl font-semibold text-foreground-900 text-center mb-3">
              {aboutData.brandIntro.title}
            </h2>
            <div className="space-y-5 mt-8">
              {aboutData.brandIntro.paragraphs.map((p, i) => (
                <p key={i} className="text-sm md:text-base text-foreground-600 leading-relaxed">
                  {p}
                </p>
              ))}
            </div>
          </div>
        </section>

        {/* Philosophy */}
        <section className="w-full px-6 md:px-10 py-14 md:py-20 bg-background-100">
          <div className="max-w-6xl mx-auto">
            <h2 className="font-heading text-2xl md:text-3xl font-semibold text-foreground-900 text-center mb-12">
              {aboutData.philosophy.title}
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
              {aboutData.philosophy.items.map((item) => (
                <div key={item.title} className="bg-background-50 rounded-lg p-6 text-center">
                  <div className="w-14 h-14 mx-auto mb-4 flex items-center justify-center rounded-full bg-accent-100">
                    <i className={`${item.icon} text-2xl text-accent-600`}></i>
                  </div>
                  <h3 className="font-heading text-lg font-semibold text-foreground-900 mb-2">{item.title}</h3>
                  <p className="text-xs md:text-sm text-foreground-500 leading-relaxed">{item.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Timeline */}
        <section className="w-full px-6 md:px-10 py-14 md:py-20 bg-background-50">
          <div className="max-w-4xl mx-auto">
            <h2 className="font-heading text-2xl md:text-3xl font-semibold text-foreground-900 text-center mb-12">
              品牌历程
            </h2>
            <div className="relative">
              <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-px bg-background-300/60 -translate-x-1/2"></div>
              <div className="space-y-8">
                {aboutData.timeline.map((item, index) => (
                  <div
                    key={item.year}
                    className={`relative flex items-start gap-6 ${
                      index % 2 === 0 ? "md:flex-row" : "md:flex-row-reverse"
                    }`}
                  >
                    <div className="hidden md:block flex-1"></div>
                    <div className="relative z-10 w-8 h-8 flex items-center justify-center rounded-full bg-accent-500 text-background-50 flex-shrink-0">
                      <span className="text-xs font-bold">{item.year.slice(2)}</span>
                    </div>
                    <div className="flex-1 bg-background-100 rounded-lg p-4 md:p-5">
                      <span className="text-xs font-bold text-accent-600">{item.year}</span>
                      <p className="text-sm text-foreground-600 mt-1">{item.event}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Reputation */}
        <section id="reputation" className="w-full px-6 md:px-10 py-14 md:py-20 bg-background-100">
          <div className="max-w-6xl mx-auto">
            <h2 className="font-heading text-2xl md:text-3xl font-semibold text-foreground-900 text-center mb-3">
              {aboutData.reputation.title}
            </h2>
            <p className="text-sm text-foreground-500 text-center mb-12">来自真实用户的声音</p>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
              {aboutData.reputation.reviews.map((review, index) => (
                <div key={index} className="bg-background-50 rounded-lg p-5 md:p-6">
                  <div className="flex items-center gap-1 mb-3">
                    {Array.from({ length: 5 }).map((_, i) => (
                      <i
                        key={i}
                        className={`text-sm ${i < review.rating ? "ri-star-fill text-accent-500" : "ri-star-line text-foreground-300"}`}
                      ></i>
                    ))}
                  </div>
                  <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                    &ldquo;{review.content}&rdquo;
                  </p>
                  <div className="flex items-center justify-between pt-3 border-t border-background-200/60">
                    <div>
                      <p className="text-sm font-medium text-foreground-800">{review.name}</p>
                      <p className="text-xs text-foreground-400">{review.location}</p>
                    </div>
                    <span className="text-xs px-2 py-1 bg-secondary-100 text-secondary-600 rounded-full">
                      {review.product}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Contact */}
        <section id="contact" className="w-full px-6 md:px-10 py-14 md:py-20 bg-background-50">
          <div className="max-w-6xl mx-auto">
            <h2 className="font-heading text-2xl md:text-3xl font-semibold text-foreground-900 text-center mb-3">
              {aboutData.contact.title}
            </h2>
            <p className="text-sm text-foreground-500 text-center mb-12">{aboutData.contact.description}</p>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16">
              <div>
                <div className="space-y-6">
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-100 flex-shrink-0">
                      <i className="ri-map-pin-line text-lg text-primary-600"></i>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-foreground-800 mb-1">地址</p>
                      <p className="text-sm text-foreground-500">{aboutData.contact.address}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-100 flex-shrink-0">
                      <i className="ri-phone-line text-lg text-primary-600"></i>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-foreground-800 mb-1">电话</p>
                      <p className="text-sm text-foreground-500">{aboutData.contact.phone}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-100 flex-shrink-0">
                      <i className="ri-mail-line text-lg text-primary-600"></i>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-foreground-800 mb-1">邮箱</p>
                      <p className="text-sm text-foreground-500">{aboutData.contact.email}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-100 flex-shrink-0">
                      <i className="ri-time-line text-lg text-primary-600"></i>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-foreground-800 mb-1">工作时间</p>
                      <p className="text-sm text-foreground-500">{aboutData.contact.hours}</p>
                    </div>
                  </div>
                </div>
                <div className="mt-8">
                  <p className="text-sm font-medium text-foreground-800 mb-3">关注我们</p>
                  <div className="flex flex-wrap gap-3">
                    {aboutData.contact.socials.map((s) => (
                      <span key={s.name} className="text-xs px-4 py-2 bg-secondary-100 text-secondary-700 rounded-full">
                        {s.name}：{s.value}
                      </span>
                    ))}
                  </div>
                </div>
              </div>

              <form
                onSubmit={handleContactSubmit}
                data-readdy-form
                className="bg-background-100 rounded-lg p-6 md:p-8"
              >
                <h3 className="font-heading text-lg font-semibold text-foreground-900 mb-6">发送留言</h3>
                <div className="anti-spam-wrapper">
                  <input type="text" name="mobile_alt" tabIndex={-1} autoComplete="off" aria-hidden="true" readOnly />
                </div>
                <div className="space-y-4">
                  <div>
                    <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-700 mb-1.5">
                      您的姓名
                    </label>
                    <input
                      id="contact-name"
                      type="text"
                      name="name"
                      value={contactForm.name}
                      onChange={(e) => setContactForm({ ...contactForm, name: e.target.value })}
                      required
                      className="w-full px-4 py-2.5 bg-background-50 border border-background-300/60 rounded-lg text-sm text-foreground-800 placeholder-foreground-300 outline-none focus:border-primary-400 transition-colors"
                      placeholder="请输入您的姓名"
                    />
                  </div>
                  <div>
                    <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-700 mb-1.5">
                      电子邮箱
                    </label>
                    <input
                      id="contact-email"
                      type="email"
                      name="email"
                      value={contactForm.email}
                      onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })}
                      required
                      className="w-full px-4 py-2.5 bg-background-50 border border-background-300/60 rounded-lg text-sm text-foreground-800 placeholder-foreground-300 outline-none focus:border-primary-400 transition-colors"
                      placeholder="请输入您的邮箱"
                    />
                  </div>
                  <div>
                    <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-700 mb-1.5">
                      留言内容
                    </label>
                    <textarea
                      id="contact-message"
                      name="message"
                      value={contactForm.message}
                      onChange={(e) => setContactForm({ ...contactForm, message: e.target.value })}
                      required
                      rows={4}
                      maxLength={500}
                      className="w-full px-4 py-2.5 bg-background-50 border border-background-300/60 rounded-lg text-sm text-foreground-800 placeholder-foreground-300 outline-none focus:border-primary-400 transition-colors resize-none"
                      placeholder="请输入您的留言（不超过500字）"
                    ></textarea>
                    <p className="text-xs text-foreground-400 mt-1 text-right">{contactForm.message.length}/500</p>
                  </div>
                  <button
                    type="submit"
                    disabled={contactStatus === "submitting"}
                    className="w-full py-3 bg-primary-500 text-background-50 rounded-lg font-medium text-sm hover:bg-primary-600 transition-all duration-300 whitespace-nowrap cursor-pointer disabled:opacity-60"
                  >
                    {contactStatus === "submitting" ? "发送中..." : "发送留言"}
                  </button>
                </div>
                {contactStatus === "success" && (
                  <p className="mt-4 text-sm text-accent-700 font-medium text-center">留言发送成功！我们会尽快回复您。</p>
                )}
                {contactStatus === "error" && (
                  <p className="mt-4 text-sm text-primary-600 font-medium text-center">{contactError}</p>
                )}
              </form>
            </div>
          </div>
        </section>
      </main>
      <Footer />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@graph": [
              {
                "@type": "BreadcrumbList",
                itemListElement: [
                  { "@type": "ListItem", position: 1, name: "首页", item: "https://meirouzhixiang.com" },
                  { "@type": "ListItem", position: 2, name: "关于我们" },
                ],
              },
              {
                "@type": "Organization",
                name: "媚肉之香",
                url: "https://meirouzhixiang.com",
                description: "融合东方香氛美学与肌肤养护精髓的高端感官体验品牌",
                foundingDate: "2019",
                contactPoint: {
                  "@type": "ContactPoint",
                  telephone: "0571-8888-6666",
                  email: "hello@meirouzhixiang.com",
                  contactType: "customer service",
                  availableLanguage: ["Chinese"],
                },
                address: {
                  "@type": "PostalAddress",
                  streetAddress: "灵隐路18号",
                  addressLocality: "杭州",
                  addressRegion: "浙江",
                  addressCountry: "CN",
                },
              },
              {
                "@type": "WebPage",
                url: "https://meirouzhixiang.com/about",
                name: "关于我们 - 媚肉之香",
                description: "了解媚肉之香的品牌故事、理念与口碑评价",
              },
            ],
          }),
        }}
      />
    </>
  );
}